import { useState, useEffect, useRef } from 'react';
import { promotionApi } from '../../../api/promotion';
import type { BasketItem } from '../page';

interface OrderDiscount {
  type: 'percentage' | 'fixed';
  value: number;
  coupon?: {
    id: number;
    code: string;
    name?: string;
    discountAmount: number;
    discountTypeId: number;
    affiliatorId: number | null;
  };
}

interface CouponDetails {
  id: number;
  code: string;
  name?: string;
  discountAmount: number;
  discountTypeId: number;
  affiliatorId: number | null;
}

interface OrderDiscountModalProps {
  onClose: () => void;
  onApply: (discount: OrderDiscount) => void;
  currentDiscount: OrderDiscount | null;
  amountBeforeDiscount: number;
  lineItemDiscount: number;
  taxBeforeOrderDiscount: number;
  basketItems: BasketItem[];
  customerId: number | null;
}

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatAmount = (value: number): string => value.toFixed(2);

const DISCOUNT_TYPE_OPTIONS = [
  { type: 'percentage', symbol: '%', label: 'Percentage' },
  { type: 'fixed', symbol: '৳', label: 'Fixed Amount' },
] as const;

export default function OrderDiscountModal({
  onClose,
  onApply,
  currentDiscount,
  amountBeforeDiscount,
  lineItemDiscount,
  taxBeforeOrderDiscount,
  basketItems,
  customerId,
}: OrderDiscountModalProps) {
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>(
    currentDiscount?.type || 'percentage'
  );
  const [discountValue, setDiscountValue] = useState<string>(
    currentDiscount?.value && currentDiscount.value > 0 ? currentDiscount.value.toString() : ''
  );
  const [error, setError] = useState<string>('');
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  const typeMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!typeMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (!typeMenuRef.current?.contains(e.target as Node)) setTypeMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [typeMenuOpen]);

  // Coupon state — initialise from existing discount if user re-opens the modal
  const [couponCode, setCouponCode] = useState<string>(currentDiscount?.coupon?.code || '');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [couponDetails, setCouponDetails] = useState<CouponDetails | null>(
    currentDiscount?.coupon
      ? {
          id: currentDiscount.coupon.id ?? 0,
          code: currentDiscount.coupon.code,
          name: currentDiscount.coupon.name,
          discountAmount: currentDiscount.coupon.discountAmount,
          discountTypeId: currentDiscount.coupon.discountTypeId ?? 0,
          affiliatorId: currentDiscount.coupon.affiliatorId ?? null,
        }
      : null
  );

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const grossAmount = Math.max(0, toNumber(amountBeforeDiscount) - toNumber(lineItemDiscount));
  const inputValue = toNumber(discountValue);
  // Raw manual amount — used for case-1 validation (block coupon if already 100%)
  const discountAmount =
    discountType === 'percentage'
      ? grossAmount * (Math.min(Math.max(inputValue, 0), 100) / 100)
      : Math.min(Math.max(inputValue, 0), grossAmount);
  const rawCouponAmount = couponDetails?.discountAmount ?? 0;
  // Effective amounts following coupon/discount interaction rules:
  // Percentage (case 3): coupon comes first, percentage applies to (gross - coupon)
  // Fixed (case 2): fixed applies to full gross, coupon fills the remaining gap
  let effectiveManualAmount: number;
  let couponDiscountAmount: number;
  if (discountType === 'percentage') {
    const manualBase = Math.max(0, grossAmount - rawCouponAmount);
    effectiveManualAmount = manualBase * (Math.min(Math.max(inputValue, 0), 100) / 100);
    couponDiscountAmount = rawCouponAmount;
  } else {
    effectiveManualAmount = discountAmount;
    couponDiscountAmount = Math.max(0, Math.min(rawCouponAmount, grossAmount - effectiveManualAmount));
  }
  const totalDiscountAmount = effectiveManualAmount + couponDiscountAmount;
  const previewTax = Math.max(0, toNumber(taxBeforeOrderDiscount));
  const previewNet = Math.max(0, grossAmount + previewTax - totalDiscountAmount);

  const handleApplyCoupon = async () => {
    const code = couponCode.trim();
    if (!code || grossAmount <= 0) return;
    // Case 1: if manual discount already covers 100% of gross, coupon cannot be applied
    if (discountAmount >= grossAmount) {
      setCouponError('Discount is already 100%. Coupon cannot be applied.');
      return;
    }
    setCouponLoading(true);
    setCouponError('');
    try {
      const items = basketItems.map((item) => ({
        id: item.itemId ?? item.line,
        amount: item.total,
        brandId: item.brandId ?? null,
        categoryIds: item.categoryIds ?? [],
        manufacturId: item.manufacturerId ?? null,
      }));
      const response = await promotionApi.applyCoupon({
        items,
        code,
        subTotal: grossAmount,
        grandTotal: Math.max(0, grossAmount - discountAmount) + previewTax,
        customerId,
      });
      if (!response?.succeeded) {
        throw new Error(response?.message || 'Invalid coupon code');
      }
      const result = response.data;
      const value = Number(result?.discountValue ?? result?.value ?? 0);
      const amount = Math.max(0, Math.min(value, grossAmount));
      setCouponDetails({
        id: Number(result?.id ?? 0),
        code,
        name: result?.name ?? result?.code ?? code,
        discountAmount: amount,
        discountTypeId: Number(result?.discountType?.id ?? result?.discountTypeId ?? 0),
        affiliatorId: result?.affiliatorId ?? null,
      });
    } catch (err: any) {
      setCouponError(err?.message || 'Invalid or expired coupon code');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleClearCoupon = () => {
    setCouponCode('');
    setCouponDetails(null);
    setCouponError('');
  };

  const handleApply = () => {
    // Allow apply if only coupon (no manual discount required)
    if (!discountValue && couponDetails) {
      onApply({
        type: discountType,
        value: 0,
        coupon: {
          id: couponDetails.id,
          code: couponDetails.code,
          name: couponDetails.name,
          discountAmount: couponDetails.discountAmount,
          discountTypeId: couponDetails.discountTypeId,
          affiliatorId: couponDetails.affiliatorId,
        },
      });
      onClose();
      return;
    }

    const value = parseFloat(discountValue);

    if (isNaN(value) || value <= 0) {
      setError('Please enter a valid discount amount');
      return;
    }

    if (discountType === 'percentage' && value > 100) {
      setError('Percentage cannot exceed 100%');
      return;
    }
    if (discountType === 'fixed' && value > grossAmount) {
      setError('Fixed discount cannot exceed gross amount');
      return;
    }

    const discountPayload: OrderDiscount = {
      type: discountType,
      value: value,
      ...(couponDetails
        ? {
            coupon: {
              id: couponDetails.id,
              code: couponDetails.code,
              name: couponDetails.name,
              discountAmount: couponDetails.discountAmount,
              discountTypeId: couponDetails.discountTypeId,
              affiliatorId: couponDetails.affiliatorId,
            },
          }
        : {}),
    };

    onApply(discountPayload);
    onClose();
  };

  const canApply = (inputValue > 0 && !error) || couponDetails !== null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Order Discount</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Coupon Code section disabled for now — may be revisited in the future. */}
          {false && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Coupon Code
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => { setCouponCode(e.target.value); setCouponError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && !couponDetails) handleApplyCoupon(); }}
                placeholder="Enter coupon code"
                readOnly={!!couponDetails}
                className={`flex-1 px-3 py-2 border-2 rounded-lg text-sm focus:outline-none ${
                  couponDetails
                    ? 'border-green-400 bg-green-50 text-green-800'
                    : couponError
                    ? 'border-red-400 focus:border-red-500'
                    : 'border-gray-300 focus:border-orange-500'
                }`}
              />
              {!couponDetails ? (
                <button
                  onClick={handleApplyCoupon}
                  disabled={!couponCode.trim() || couponLoading || grossAmount <= 0 || !customerId}
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {couponLoading ? (
                    <i className="ri-loader-4-line animate-spin"></i>
                  ) : (
                    'Apply'
                  )}
                </button>
              ) : (
                <button
                  onClick={handleClearCoupon}
                  className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer"
                >
                  Clear
                </button>
              )}
            </div>
            {!customerId && !couponDetails && (
              <p className="mt-1.5 text-xs text-amber-600 flex items-center gap-1">
                <i className="ri-user-line"></i>
                A customer must be selected to apply a coupon.
              </p>
            )}
            {couponDetails && (
              <div className="mt-1.5 flex items-center gap-1.5 text-green-600">
                <i className="ri-checkbox-circle-fill text-sm"></i>
                <span className="text-xs font-medium">
                  {couponDetails.name || couponDetails.code} applied — -{formatAmount(couponDetails.discountAmount)}
                </span>
              </div>
            )}
            {couponError && (
              <p className="mt-1 text-xs text-red-600">
                <i className="ri-error-warning-line mr-1"></i>
                {couponError}
              </p>
            )}
          </div>
          )}

          {/* Manual Input */}
          <div>
            <div className="flex items-center justify-between gap-4">
              <label className="text-sm font-semibold text-gray-700">
                {discountType === 'percentage' ? 'Discount Percentage' : 'Discount Amount'}
              </label>
              <div className="flex items-stretch gap-2">
                <div ref={typeMenuRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setTypeMenuOpen((open) => !open)}
                    className={`h-full flex items-center gap-1 px-3 border-2 rounded-lg bg-white text-sm font-semibold text-gray-700 transition-colors cursor-pointer ${
                      typeMenuOpen ? 'border-orange-500' : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <span className="w-4 text-center">{discountType === 'percentage' ? '%' : '৳'}</span>
                    <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${typeMenuOpen ? 'rotate-180' : ''}`}></i>
                  </button>
                  {typeMenuOpen && (
                    <div className="absolute left-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-10">
                      {DISCOUNT_TYPE_OPTIONS.map((opt) => (
                        <button
                          key={opt.type}
                          type="button"
                          onClick={() => {
                            setDiscountType(opt.type);
                            setError('');
                            setTypeMenuOpen(false);
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors cursor-pointer ${
                            discountType === opt.type
                              ? 'bg-orange-50 text-orange-600 font-semibold'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <span className="w-4 text-center font-bold">{opt.symbol}</span>
                          <span className="flex-1">{opt.label}</span>
                          {discountType === opt.type && <i className="ri-check-line"></i>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="w-40 border-2 border-gray-300 rounded-lg overflow-hidden focus-within:border-orange-500">
                  <input
                    type="number" onWheel={(e) => e.currentTarget.blur()}
                    autoFocus
                    value={discountValue}
                    onChange={(e) => {
                      setDiscountValue(e.target.value);
                      setError('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleApply();
                      }
                    }}
                    placeholder="0"
                    className="w-full px-3 py-1 border-0 focus:outline-none text-right text-base font-bold text-gray-900 placeholder:text-gray-300"
                    step={discountType === 'percentage' ? '1' : '0.01'}
                    min="0"
                    max={discountType === 'percentage' ? '100' : undefined}
                  />
                </div>
              </div>
            </div>
            {error && (
              <p className="mt-2 text-sm text-red-600 text-right">
                <i className="ri-error-warning-line mr-1"></i>
                {error}
              </p>
            )}
          </div>

          {/* Summary + Preview */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1.5 text-sm">
            <div className="flex items-center justify-between text-gray-700">
              <span>Amount Before Discount</span>
              <span className="font-semibold">{formatAmount(amountBeforeDiscount)}</span>
            </div>
            <div className="flex items-center justify-between text-gray-700">
              <span>Line Item Discount</span>
              <span className="font-semibold">{formatAmount(lineItemDiscount)}</span>
            </div>
            <div className="flex items-center justify-between text-gray-700 border-t border-gray-200 pt-1.5">
              <span>Gross Amount</span>
              <span className="font-bold">{formatAmount(grossAmount)}</span>
            </div>
            {(discountAmount > 0 || couponDiscountAmount > 0) && (
              <div className="flex items-center justify-between text-orange-600 border-t border-gray-200 pt-1.5">
                <span className="font-semibold">
                  Discount
                  {couponDetails && couponDiscountAmount > 0 && (
                    <span className="font-normal text-orange-400"> (Coupon {formatAmount(couponDiscountAmount)})</span>
                  )}
                </span>
                <span className="font-semibold">-{formatAmount(totalDiscountAmount)}</span>
              </div>
            )}
            <div className="flex items-center justify-between text-gray-700">
              <span>Total VAT</span>
              <span className="font-semibold">{formatAmount(previewTax)}</span>
            </div>
            <div className="flex items-center justify-between border-t border-gray-200 pt-1.5 text-gray-900">
              <span className="font-semibold">Net Amount</span>
              <span className="text-lg font-bold">{formatAmount(previewNet)}</span>
            </div>
          </div>
        </div>

        <div className="flex gap-3 px-5 py-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={!canApply}
            className={`flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer ${!canApply ? 'opacity-50 cursor-not-allowed' : 'hover:bg-orange-600'}`}
          >
            <i className="ri-check-line mr-2"></i>
            Apply Discount
          </button>
        </div>
      </div>
    </div>
  );
}
